import React, { useEffect, useState } from 'react';

import BudgetByDimensionReportTable from './BudgetByDimensionReportTable';
import { useBudgetByDimensionReportContext } from './context/BudgetByDimensionReportContext';
import { BudgetByDimensionReportTableProvider } from './context/BudgetByDimensionReportTableContext';

import Alert from 'CommonComponents/Alert';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';

/**
 * One archived preview, rendered on screen from the artifact stored with its
 * row rather than from the criteria panel's current selection.
 *
 * The endpoint answers 200 with a status in every case, so a row whose artifact
 * has aged out, is still building, or failed says so here instead of rendering
 * an empty table.
 */
const ArchivedDimensionPreviewView = ({ report }) => {
    const { fetchPreviewReport, dimension, isVikEnabled } = useBudgetByDimensionReportContext();

    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState(null);
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        let current = true;

        setLoading(true);
        setNotice(null);
        setPayload(null);

        fetchPreviewReport(report.id)
            .then((result) => {
                if (!current) {
                    return;
                }

                if (result?.status === 'cached') {
                    setPayload(result);
                } else {
                    // Sanitised server-side — the raw reason can carry a failing
                    // SQL statement — so render it as it arrived.
                    setNotice({
                        type: result?.status === 'failed' ? 'error' : 'info',
                        message: result?.message ?? $t('This report is no longer available. Generate it again to view it.'),
                    });
                }
            })
            .catch(() => {
                if (current) {
                    setNotice({ type: 'error', message: $t('Failed to load report data.') });
                }
            })
            .finally(() => {
                if (current) {
                    setLoading(false);
                }
            });

        return () => {
            current = false;
        };
    }, [report.id, fetchPreviewReport]);

    if (loading) {
        return (
            <div className="tw-py-12">
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    if (notice) {
        return (
            <Alert type={notice.type} role={notice.type === 'error' ? 'alert' : 'status'}>
                {notice.message}
            </Alert>
        );
    }

    // The preview is stored keyed per dimension:
    // Venue report: { data: ..., venues: [...] }
    // Event report: { data: ..., sports: [...] }
    const allDimensions = payload?.venues ?? payload?.sports ?? [];
    const scopeIds = report.criteria?.scope_ids ?? [];
    const dimensions = scopeIds.length > 0 ? allDimensions.filter((dim) => scopeIds.includes(dim.id)) : allDimensions;

    return (
        <BudgetByDimensionReportTableProvider
            criteria={report.criteria}
            report={payload?.data ?? null}
            dimensions={dimensions}
            loading={false}
            isVikEnabled={isVikEnabled}
            dimension={dimension}
        >
            <BudgetByDimensionReportTable />
        </BudgetByDimensionReportTableProvider>
    );
};

export default ArchivedDimensionPreviewView;
